import { zodResolver } from "@hookform/resolvers/zod";
import {
    Modal,
    SelectField,
    TextAreaField,
    TextInputField,
    ToggleField,
    useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiResponse,
    CreateCurriculumPayload,
    CreateCurriculumPayloadSchema,
    CreateCurriculumResponse,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, Text } from "@twinkltech/web-ui";
import { useId, useRef, useState } from "react";
import { SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { CurriculumAccessTogglePrompt } from "@/components/CurriculumAccessTogglePrompt/CurriculumAccessTogglePrompt";
import { GENERIC_CURRICULUM_FORM_NS } from "@/components/GenericCurriculumModal/GenericCurriculumModal.i18n";

import { usePreserveElementHeight } from "@/hooks/usePreserveElementHeight/usePreserveElementHeight";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";
import { captureAppError } from "@/utils/monitoring/sentry";

import { REQUIRED_FIELD_MESSAGE } from "@/utils/validation-messages";

import { NEW_CURRICULUM_MODAL_NS } from "../../NewCurriculumModal.i18n";

const CurriculumFormSchema = z.object({
  displayLabel: z.string().min(1, REQUIRED_FIELD_MESSAGE).max(120),
  description: z.string().max(500).optional(),
  status: z.enum(["active", "archived"]),
  progress: z.enum(["not_yet_planned", "planned", "planning_in_progress"]),
  access: z.boolean(),
});

type CurriculumFormData = z.infer<typeof CurriculumFormSchema>;

export const SchoolNewCurriculumModal = ({
  isLoading,
  onHandleSubmit,
  open,
  setOpen,
}: {
  isLoading: boolean;
  open: boolean;
  onHandleSubmit: (params: {
    payload: CreateCurriculumPayload;
  }) => ApiResponse<CreateCurriculumResponse>;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation(NEW_CURRICULUM_MODAL_NS);
  const { incrementEngagementCount } = useFeedback();

  const { click } = useTwinklAnalytics();

  const onHandleOnClose = () => {
    setOpen(false);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addErrorToast, addSuccessToast } = useToasts();

  // Accessibility IDs
  const formId = useId();
  const currDescriptionInputId = useId();
  const currVisibilityInputId = useId();
  const currPlanningProgressInputId = useId();

  const formDefaultValues: CurriculumFormData = {
    displayLabel: "",
    description: "",
    status: "active",
    progress: "not_yet_planned",
    access: false,
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<CurriculumFormData, unknown, CurriculumFormData>({
    resolver: zodResolver<CurriculumFormData, unknown, CurriculumFormData>(
      CurriculumFormSchema
    ),
    defaultValues: formDefaultValues,
  });

  // Note: Need to use a controller for the Headless UI custom components
  const { field: accessField } = useController({
    control,
    name: "access",
  });
  const { field: progressField } = useController({
    control,
    name: "progress",
  });
  const { field: statusField } = useController({
    control,
    name: "status",
  });

  const onSubmit = async (data: CreateCurriculumPayload) => {
    const result = CreateCurriculumPayloadSchema.safeParse(data);

    if (result.success) {
      return await onHandleSubmit({
        payload: result.data,
      });
    }

    console.error("Form data validation failed:", result.error);
    return result.error;
  };

  const onFormSubmit: SubmitHandler<CurriculumFormData> = async (
    data,
    event
  ) => {
    event?.preventDefault();

    if (isLoading || isSubmitting) {
      return;
    }

    incrementEngagementCount();

    const mappedData: CreateCurriculumPayload = {
      displayLabel: data.displayLabel,
      description: data.description,
      status: data.status,
      progress: data.progress,
      // Note: If we set it to specifically look for private, and when the value is not set it will remain public.
      // When a public curriculum cannot be set back to private. We want to ensure that it's only public when intentionally set to be so.
      // Checking something is _not_ public is more robust in this use case and creates a better user experience.
      private: !data.access,
    };

    setIsSubmitting(true);
    const result = await onSubmit(mappedData);

    if (result instanceof Error) {
      captureAppError(result, {
        action: "create",
        area: "curriculum",
        extra: {
          component: "SchoolNewCurriculumModal",
        },
      });
      addErrorToast({
        message: "Update failed",
      });
    } else {
      addSuccessToast({
        message: "Curriculum created successfully",
      });
      onHandleOnClose();
    }

    click("click_add_new_modal_cta", {
      ctaCopy: "Add New Curriculum",
    });

    setIsSubmitting(false);
  };

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isAccessInfoVisible, setIsAccessInfoVisible] = useState(false);
  const { preserveHeight, styles: modalBodyStyles } =
    usePreserveElementHeight(modalBodyRef);

  const toggleAccessInfoVisibility = (show: boolean) => {
    preserveHeight(show);
    setIsAccessInfoVisible(show);
  };

  const onClickClose = () => {
    click("click_add_new_modal_cta", {
      ctaCopy: "Cancel",
    });
    onHandleOnClose();
  };

  return (
    <Modal hasCloseButton open={open} width="md" onClose={onClickClose}>
      <Modal.Header>
        <Modal.Title>{t("title", { ns: NEW_CURRICULUM_MODAL_NS })}</Modal.Title>
      </Modal.Header>
      <Modal.Body
        ref={modalBodyRef}
        className="mb-2 max-h-[75vh] overflow-y-auto"
        style={modalBodyStyles}
      >
        <form
          className="py-2"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div role="group">
            <Text className="mb-3" variant="xl-medium">
              {t("access-group.title")}
            </Text>
            <ToggleField
              checked={accessField.value}
              infoText={
                accessField.value
                  ? t("access-group.school-access-description")
                  : "Private curricula can only be viewed and edited by you"
              }
              label={t("access-group.label")}
              options={{
                on: "Yes (shared with School)",
                off: "No (Private)",
              }}
              {...register("access")}
              onChange={(value) => {
                accessField.onChange(value);
                toggleAccessInfoVisibility(value);
              }}
            />
            {isAccessInfoVisible && (
              <CurriculumAccessTogglePrompt
                onCancel={() => {
                  toggleAccessInfoVisibility(false);
                  accessField.onChange(false);
                }}
                onConfirm={() => {
                  toggleAccessInfoVisibility(false);
                }}
              />
            )}
          </div>

          <div className="my-6 4 border-b" />

          <div className="space-y-3" role="group">
            <Text className="mb-3" variant="xl-medium">
              {t("details-group.title")}
            </Text>
            <TextInputField
              disabled={isAccessInfoVisible}
              infoText={t("inputs.name.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.name.label", { ns: GENERIC_CURRICULUM_FORM_NS })}
              maxLength={120}
              placeholder={t("inputs.name.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("displayLabel", { required: true })}
              errorText={errors.displayLabel?.message}
            />
            <TextAreaField
              disabled={isAccessInfoVisible}
              id={currDescriptionInputId}
              infoText={t("inputs.description.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.description.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              maxLength={500}
              placeholder={t("inputs.description.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("description")}
              errorText={errors.description?.message}
            />
            <SelectField
              disabled={isAccessInfoVisible}
              id={currVisibilityInputId}
              label={t("inputs.status.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
              {...statusField}
              defaultValue={formDefaultValues.status}
              errorText={errors.status?.message}
            />
            <SelectField
              disabled={isAccessInfoVisible}
              id={currPlanningProgressInputId}
              label={t("inputs.progress.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "not_yet_planned", label: "Not yet planned" },
                {
                  value: "planning_in_progress",
                  label: "Planning in progress",
                },
                { value: "planned", label: "Planned" },
              ]}
              {...progressField}
              defaultValue={formDefaultValues.progress}
              errorText={errors.progress?.message}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-2">
        <Button
          disabled={isAccessInfoVisible || isSubmitting}
          type="button"
          variant="outlined"
          onClick={onClickClose}
        >
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={isLoading || !open || isSubmitting || isAccessInfoVisible}
          form={formId}
          variant="primary"
        >
          {t("actions.save", { ns: NEW_CURRICULUM_MODAL_NS })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
